import { notifications } from '@mantine/notifications';

import { ApiResponseError } from '@/apis/http';
import { OwnerInfo, PermissionErrorPayload, PermissionErrorType } from '@/types/permission-error';

/**
 * Check if an API error is a permission error with structured payload
 */
export function isPermissionError(error: ApiResponseError): boolean {
  return error?.code === 403 && error?.data?.permissionError !== undefined;
}

/**
 * Extract permission error payload from an API error
 */
export function getPermissionErrorPayload(error: ApiResponseError): PermissionErrorPayload | null {
  if (!isPermissionError(error)) {
    return null;
  }
  return error.data?.permissionError || null;
}

/**
 * Format owner contact information for display
 */
export function formatOwnerContact(owner: OwnerInfo): string {
  return `${owner.name} (${owner.email})`;
}

/**
 * Show a notification for a permission error
 */
export function showPermissionNotification(payload: PermissionErrorPayload): void {
  notifications.show({
    title: getPermissionErrorTitle(payload.errorType),
    message: payload.message,
    color: 'red',
    autoClose: 8000,
  });
}

/**
 * Get a user-friendly title for a permission error type
 */
function getPermissionErrorTitle(errorType: PermissionErrorType): string {
  switch (errorType) {
    case PermissionErrorType.NO_ACCESS_TO_CHATBOT:
      return 'Access Denied';
    case PermissionErrorType.NO_ACCESS_TO_FEATURE:
      return 'Feature Restricted';
    case PermissionErrorType.ACTION_NOT_PERMITTED:
      return 'Action Not Permitted';
    case PermissionErrorType.PERMISSIONS_NOT_CONFIGURED:
      return 'Permissions Not Set Up';
    default:
      return 'Permission Error';
  }
}

/**
 * Permission message templates for frontend use
 */
export const PermissionMessages = {
  viewOnlyAccess: (owner: OwnerInfo): string =>
    `View Only - You cannot make changes. Contact ${owner.name} (${owner.email}) to request edit permissions.`,

  noAccessToSection: (owner: OwnerInfo): string =>
    `You don't have access to this section. Contact ${owner.name} (${owner.email}) to request access.`,

  actionRestricted: (action: string, currentPerms: string[], owner: OwnerInfo): string =>
    `You don't have permission to ${action}. Current permissions: ${currentPerms.length > 0 ? currentPerms.join(', ') : 'None'}. Contact ${owner.name} (${owner.email}) for additional access.`,

  emptyDueToPermissions: (owner: OwnerInfo): string =>
    `No data visible - you may not have permission to view this content. Contact ${owner.name} (${owner.email}) if you should have access.`,
};
